"use client";

import { useEffect, useMemo, useRef } from "react";
import { useInView } from "react-intersection-observer";
import { BadgeIcon, type IconVariant } from "@/components/ui/badgeIcon";
import { DEFAULT_USE_IN_VIEW_OPTIONS } from "@/constants/tagular/main";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import { resolvedGeoPricingText } from "./utils/resolvedGeoPricingText";
import { cn } from "@/lib/utils";
import type { GeoPricingLocation } from "./types";

type HeroCardBadgesProps = {
  badgeText: string;
  badgeIcon?: IconVariant;
  subBadgeText?: string;
  percentageDiscount: number;
  countryName?: string;
  location: GeoPricingLocation;
};

export default function HeroCardBadges({
  badgeIcon = "none",
  badgeText,
  subBadgeText,
  percentageDiscount,
  countryName,
  location,
}: HeroCardBadgesProps) {
  const hasViewedRef = useRef(false);
  const { ref: inViewRef, inView } = useInView(DEFAULT_USE_IN_VIEW_OPTIONS);
  const tokens = { percentageDiscount, countryName };
  const resolvedBadgeText =
    resolvedGeoPricingText(badgeText, tokens) ?? badgeText;
  const resolvedSubBadgeText = resolvedGeoPricingText(subBadgeText, tokens);

  const badgeWebElement = useMemo(
    () => ({
      elementType: "TEXT" as const,
      htmlId: "geo-hero-card-badge",
      location,
      name: "geo-hero-card-badge",
      text: resolvedBadgeText,
    }),
    [location, resolvedBadgeText],
  );

  const { elementViewed } = useElementEvents({ webElement: badgeWebElement });

  useEffect(() => {
    if (inView && !hasViewedRef.current) {
      hasViewedRef.current = true;
      elementViewed();
    }
  }, [inView, elementViewed]);

  return (
    <div
      ref={inViewRef}
      className="flex flex-row gap-2 items-start justify-center"
    >
      <BadgeIcon
        key={badgeIcon}
        icon={badgeIcon}
        color="primaryDark"
        withBorder
        iconSize={14}
        className={cn(
          "shrink-0 whitespace-nowrap gap-2",
          "md:border-primary-500 md:bg-primary-500 md:text-white",
        )}
      >
        <span className="text-micro">{resolvedBadgeText}</span>
      </BadgeIcon>

      {subBadgeText && (
        <BadgeIcon
          icon="none"
          color="primaryDark"
          withBorder
          iconSize={14}
          className={cn(
            "shrink-0 whitespace-nowrap gap-2",
            "md:border-primary-500 md:bg-primary-500 md:text-white",
          )}
        >
          <span className="text-micro">{resolvedSubBadgeText}</span>
        </BadgeIcon>
      )}
    </div>
  );
}
